import SearchField from '@shared/components/core/search/SearchField';
import MacroListing from '@shared/components/macro/MacroListing';
import AssociatedProgramsListing from '@shared/components/profile/listing/AssociatedProgramsListing';
import DictationListing from '@shared/components/profile/listing/DictationListing';
import HotkeysListing from '@shared/components/profile/listing/HotkeysListing';
import KeyCommandListing from '@shared/components/profile/listing/KeyCommandsListing';
import TalkbackListing from '@shared/components/profile/listing/TalkbackListing';
import EditProfileContainer from '@shared/hooks/state/EditProfileContainer';
import ProfilesContainer from '@shared/hooks/state/ProfilesContainer';
import { KeyCommandDisplayMapping } from '@shared/models/deviceProfiles/KeyCommandMapping';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

export interface IProfilePreviewPageContentProps {
    isDeepLink?: boolean;
}
let lastShareCode: string | null = null;
let lastShareProfileId: string | null = null;
let lastProfileId: string | null = null;
export default function ProfilePreviewPageContent({ isDeepLink = false }: IProfilePreviewPageContentProps) {
    const { shareCode, profileId } = useParams();
    const { t } = useTranslation();
    const editProfileContainer = EditProfileContainer.useContainer();
    const [filter, setFilter] = useState<string>('');
    const [loadingError, setLoadingError] = useState<boolean>(false);
    const { allProfilesRef } = ProfilesContainer.useContainer();

    const filteredMacros = useMemo(() => {
        if (loadingError) return [];
        return editProfileContainer.macros.filter((m) => {
            const macroName = m.name?.toLowerCase() || t('common:noName');
            const commandMatches = m.commands?.some((c) => c.command.toLowerCase().includes(filter));
            return commandMatches || macroName.includes(filter) || !filter;
        });
    }, [filter, editProfileContainer.macros, loadingError, t]);

    const filteredHotkeys = useMemo(() => {
        if (loadingError) return [];
        return editProfileContainer.hotkeys.filter((h) => {
            const hotkeyName = h.displayName?.toLowerCase().includes(filter);
            const hotkeyCommandMatches = h.command?.toLowerCase().includes(filter);
            return hotkeyCommandMatches || hotkeyName || !filter;
        });
    }, [filter, editProfileContainer.hotkeys, loadingError]);

    const filteredTalkback = useMemo(() => {
        if (loadingError) return [];
        return editProfileContainer.audioEvents.filter((a) => {
            const talkbackName = a.outputSpeech?.toLowerCase().includes(filter);
            const talkbackCommandMatches = a.commands?.some((c) => c.command.toLowerCase().includes(filter));
            return talkbackCommandMatches || talkbackName || !filter;
        });
    }, [filter, editProfileContainer.audioEvents, loadingError]);

    const filteredDictation = useMemo(() => {
        if (loadingError) return [];
        return editProfileContainer.dictationCommands.filter((d) => {
            const dictationName = d?.toLowerCase().includes(filter);
            return dictationName || !filter;
        });
    }, [filter, editProfileContainer.dictationCommands, loadingError]);

    const filteredKeyCommands = useMemo(() => {
        if (loadingError) return [];
        return editProfileContainer.commandKeyMappings.filter((k) => {
            const keyCommandName = k.displayName?.toLowerCase().includes(filter);
            const keyCommandCommandMatches = k.commands?.some((c) => c.command.toLowerCase().includes(filter));
            return keyCommandCommandMatches || keyCommandName || !filter;
        });
    }, [filter, editProfileContainer.commandKeyMappings, loadingError]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        setFilter(query);
    };

    const handleClearSearch = () => {
        setFilter('');
    };
    useEffect(() => {
        if (
            lastShareCode === shareCode &&
            shareCode != null &&
            lastShareProfileId === editProfileContainer.idRef.current
        ) {
            return;
        }
        if (lastProfileId === profileId && profileId != null && !shareCode) {
            return;
        }
        lastShareCode = shareCode ?? null;
        lastProfileId = profileId ?? null;

        const loadProfile = async () => {
            editProfileContainer.setIsPreview(true);
            if (shareCode) {
                const result = await editProfileContainer.loadPreview(shareCode);
                if (result.resultType !== 'Ok') {
                    editProfileContainer.setDefaultProfile('en-US');
                    setLoadingError(true);
                }
                lastShareProfileId = result.data?.id ?? null;
            } else if (profileId) {
                const profile = allProfilesRef.current.find((p) => p.id === profileId);
                if (profile) {
                    editProfileContainer.updateProfileState({
                        ...profile,
                        userId: undefined,
                    });
                } else {
                    editProfileContainer.loadPublicPreview(profileId);
                }
                lastShareProfileId = null;
            }
        };
        loadProfile();
    }, [profileId, shareCode]);

    function hasKeyCommands(commandKeyMappings: KeyCommandDisplayMapping[]): boolean {
        return commandKeyMappings.some((item) => item.commands.length > 0);
    }

    if (loadingError) {
        return (
            <div>
                <p>{t('common:profile.previewLoadError')}</p>
            </div>
        );
    }
    return (
        <>
            <SearchField value={filter} onChange={handleSearchChange} onClear={handleClearSearch} />

            {editProfileContainer.macros.length > 0 && <MacroListing macros={filteredMacros} />}
            {editProfileContainer.hotkeys.length > 0 && <HotkeysListing hotkeys={filteredHotkeys} />}
            {editProfileContainer.audioEvents.length > 0 && <TalkbackListing audioEvents={filteredTalkback} />}
            {editProfileContainer.dictationCommands.length > 0 && (
                <DictationListing dictationCommands={filteredDictation} />
            )}

            {hasKeyCommands(editProfileContainer.commandKeyMappings) && (
                <KeyCommandListing commandKeyMappings={filteredKeyCommands} />
            )}
            {editProfileContainer.associatedPrograms.length > 0 && (
                <AssociatedProgramsListing
                    profileName={editProfileContainer.name}
                    programNames={editProfileContainer.associatedPrograms}
                />
            )}
        </>
    );
}
